import type {
  HoverIdleSnapshot,
  NHovBranchId,
  NHovBranchRewardPreview,
  NHovCandidateTheme,
} from "../types";
import { NHOV_BRANCH_IDS } from "./nHovBranches";
import { resolveNHovCandidateOffer } from "./nHovCandidateTheme";

// Opaque to the view so i18n can substitute strings without leaking format
// concerns past the boundary.
export type NHovBranchHeadingLabels = Readonly<Record<NHovBranchId, string>>;

export interface NHovBranchCellViewModel {
  readonly branchId: NHovBranchId;
  readonly theme: NHovCandidateTheme;
  readonly headingLabel: string;
  readonly currentValueLabel: string;
  readonly freshNextValueLabel: string;
  readonly nextValueLabel: string;
  readonly freshnessWallActive: boolean;
  readonly isAvailable: boolean;
  readonly canExecute: boolean;
  readonly isLocked: boolean;
  readonly branchLevelProgressRatio: number;
}

export type NHovViewMode = "first" | "branches";

export interface NHovViewModel {
  readonly isVisible: boolean;
  readonly canExecute: boolean;
  readonly mode: NHovViewMode;
  readonly cells: readonly NHovBranchCellViewModel[];
}

export interface CreateNHovViewModelOptions {
  readonly snapshot: HoverIdleSnapshot;
  readonly headingLabels: NHovBranchHeadingLabels;
}

const HIDDEN_VIEW_MODEL: NHovViewModel = {
  isVisible: false,
  canExecute: false,
  mode: "first",
  cells: [],
};

function findPreview(
  previews: readonly NHovBranchRewardPreview[],
  branchId: NHovBranchId,
): NHovBranchRewardPreview | undefined {
  return previews.find((preview) => preview.branchId === branchId);
}

function buildCell(
  branchId: NHovBranchId,
  preview: NHovBranchRewardPreview | undefined,
  headingLabel: string,
  fallbackTheme: NHovCandidateTheme,
): NHovBranchCellViewModel {
  const isAvailable = preview?.isAvailable ?? false;
  const canExecute = preview?.canExecute ?? false;
  // Legacy snapshot shapes from older tests may omit these fields; recover the
  // locked state and default the progress bar to empty rather than full.
  const isLocked = preview?.isLocked ?? (isAvailable && !canExecute);
  const branchLevelProgressRatio = clampUnitInterval(
    preview?.branchLevelProgressRatio ?? 0,
  );
  return {
    branchId,
    theme: preview?.theme ?? fallbackTheme,
    headingLabel,
    currentValueLabel: preview?.currentValueLabel ?? "",
    freshNextValueLabel:
      preview?.freshNextValueLabel ?? preview?.nextValueLabel ?? "",
    nextValueLabel: preview?.nextValueLabel ?? "",
    freshnessWallActive: preview?.freshnessWallActive === true,
    isAvailable,
    canExecute,
    isLocked,
    branchLevelProgressRatio,
  };
}

function clampUnitInterval(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

export function createNHovViewModel(
  options: CreateNHovViewModelOptions,
): NHovViewModel {
  const { nHov } = options.snapshot;
  const isStockDepleted = nHov.executionStock?.depleted === true;
  if (!nHov.isUnlocked && !isStockDepleted) {
    return HIDDEN_VIEW_MODEL;
  }
  // Hide the panel entirely when no branch is executable so the first-NHov
  // minimum cannot surface a lone locked hgAmplify cell.
  if (!nHov.canExecute && !isStockDepleted) {
    return HIDDEN_VIEW_MODEL;
  }
  // Fallback for legacy snapshot shapes used in tests.
  const offer = nHov.currentOffer ?? resolveNHovCandidateOffer(nHov.count);

  if (nHov.count === 0 && nHov.firstExecutionBranchSelectionUnlocked !== true) {
    const preview = findPreview(nHov.rewardPreviews, "hgAmplify");
    return {
      isVisible: true,
      canExecute: nHov.canExecute,
      mode: "first",
      cells: [
        buildCell(
          "hgAmplify",
          preview,
          options.headingLabels.hgAmplify,
          offer.themesByBranchId.hgAmplify,
        ),
      ],
    };
  }

  return {
    isVisible: true,
    canExecute: nHov.canExecute,
    mode: "branches",
    cells: NHOV_BRANCH_IDS.map((branchId) =>
      buildCell(
        branchId,
        findPreview(nHov.rewardPreviews, branchId),
        options.headingLabels[branchId],
        offer.themesByBranchId[branchId],
      ),
    ),
  };
}
